import { type FormEvent, useState } from "react";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import type { SearchCategory } from "src/interfaces/ComplianceGuide/ILitigatorSearch";
import styles from "./LitigatorSearch.module.css";

interface SearchFormProps {
  onSearch: (searchText: string, categories: SearchCategory[]) => void;
  isLoading: boolean;
}

// Category definitions with their display labels and sprite icon CSS classes
const categoryOptions: {
  value: SearchCategory;
  label: string;
  iconClass: string;
  accountOwnerOnly?: boolean;
}[] = [
  { value: "caseTitle", label: "Case Title", iconClass: "ico-sm-case" },
  { value: "caseNumber", label: "Case Number", iconClass: "ico-sm-number" },
  { value: "plaintiff", label: "Plaintiff", iconClass: "ico-sm-plaintiff" },
  {
    value: "plaintiffCounsel",
    label: "Plaintiff Counsel",
    iconClass: "ico-sm-plaintiff-counsel",
  },
  { value: "defendant", label: "Defendant", iconClass: "ico-sm-defendant" },
  {
    value: "phoneNumber",
    label: "Phone Number",
    iconClass: "ico-sm-phone",
    accountOwnerOnly: true,
  },
];

const SearchForm = ({ onSearch, isLoading }: SearchFormProps) => {
  const { role } = useAppSelector(selectAuth);
  const isAccountOwner = String(role) === "3";
  const [searchText, setSearchText] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<
    SearchCategory[]
  >([]);
  const [validationError, setValidationError] = useState("");

  const handleCategoryToggle = (category: SearchCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
    // Clear validation error when user selects a category
    setValidationError("");
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (selectedCategories.length === 0) {
      setValidationError("Please select at least one search category.");
      return;
    }

    setValidationError("");
    onSearch(searchText, selectedCategories);
  };

  // Filter categories — Phone Number is limited to account owners (roleId 3)
  const visibleCategories = categoryOptions.filter(
    (cat) => !cat.accountOwnerOnly || isAccountOwner,
  );

  return (
    <form onSubmit={handleSubmit} className={styles["search-form"]}>
      <div className="row">
        <div className="col-md-7">
          <input
            type="text"
            id="litigator-search-text"
            className="form-control form-control-sm"
            placeholder="Search…"
            aria-label="Search text"
            disabled={isLoading}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <button
            type="submit"
            className="btn btn-outline-secondary btn-sm"
            disabled={isLoading}
          >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Searching...
            </>
          ) : (
            <>
              <i className="bi bi-search me-1" />
              Search
            </>
          )}
          </button>
        </div>
      </div>

      <div className={styles["category-checkboxes"]}>
        {visibleCategories.map((cat) => (
          <label key={cat.value} className={styles["category-item"]}>
            <input
              type="checkbox"
              checked={selectedCategories.includes(cat.value)}
              onChange={() => handleCategoryToggle(cat.value)}
            />
            <span className={`${styles["ico-sm"]} ${styles[cat.iconClass]}`} />
            <span>{cat.label}</span>
          </label>
        ))}
      </div>

      {validationError && (
        <div className="alert alert-danger py-2 mb-0" role="alert">
          {validationError}
        </div>
      )}
    </form>
  );
};

export default SearchForm;
